import {
  Component, OnInit, ViewEncapsulation, ViewChild, ElementRef, AfterViewInit,
  HostListener, ChangeDetectorRef, Output, EventEmitter, Input, OnChanges, SimpleChanges
} from '@angular/core';
import { Subject } from 'rxjs/internal/Subject';

import { AngularResizeEventModule, ResizedEvent } from 'angular-resize-event';
import { WalletPublicService } from '@app/shared/services';
import { Wallets, WalletSelectList } from '@app/shared/models';
import { BuySellServiceType, RequestType, TransactionType, WalletTransactionState } from '@app/shared/types';
import { take, finalize } from 'rxjs/operators';
import { InlineAlertTypes } from '@app/shared/types/inline-alert-types';
import { WalletType } from '@app/views/pages/wallets/types';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { TcCurrencyPipe } from '@app/shared/pipes';
import { TableLoadingComponent } from '../../table-loading/table-loading.component';
import { EmptyTemplateComponent } from '../../empty-template/empty-template.component';
import { InlineAlertComponent } from '../../inline-alert/inline-alert.component';

@Component({
    selector: 'tc-request-wallet',
    imports: [
        CommonModule,
        TranslateModule,
        TcCurrencyPipe,
        TableLoadingComponent,
        EmptyTemplateComponent,
        InlineAlertComponent,
        AngularResizeEventModule
    ],
    templateUrl: './request-wallet.component.html',
    styleUrls: ['./request-wallet.component.scss'],
    encapsulation: ViewEncapsulation.None
})

export class RequestWalletComponent implements OnInit, AfterViewInit, OnChanges {

  @ViewChild('wallets') walletsView: ElementRef;
  @Output() selectedWalletEmit = new EventEmitter<WalletSelectList>();

  // is buy or sell service and can be null
  @Input() buySellServiceType: BuySellServiceType;
  @Input() selectedWallet: WalletSelectList;
  @Input() removeWalletId: string;
  @Input() requestType: RequestType;
  @Input() serviceId: RequestType;
  @Input() relationId: number;
  @Input() fromWalletId: string;
  @Input() showCrypto: boolean = false;
  @Input() isNewApiForTransactionType: boolean = false;
  @Input() transactionType: TransactionType;

  // observable
  unsubscribeAll$: Subject<any>;


  // data
  fiatWalletList: WalletSelectList[] = [];
  cryptoWalletList: WalletSelectList[] = [];
  wallets: Wallets;


  // boolean
  isLoading = true;


  // types
  walletTransactionStates = WalletTransactionState;

  // style
  isMobileSize: boolean;
  walletClass: string;
  inlineAlertTypes = InlineAlertTypes;

  constructor(
    private cdr: ChangeDetectorRef,
    private walletPublicService: WalletPublicService
  ) {
  }

  /*******************************************************************
   Life Cycle
  *******************************************************************/

  ngOnInit(): void {
    this.onResize();
    this.getWallets();
    if (this.selectedWallet && this.selectedWallet.walletId !== this.removeWalletId) {
      this.selectedWalletEmit.emit(this.selectedWallet);
    }
  }

  ngAfterViewInit(): void {
    if (this.walletsView) {
      this.walletClass = this.getClass(this.walletsView.nativeElement.offsetWidth);
      this.cdr.detectChanges();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.selectedWallet = changes.selectedWallet.currentValue;
  }

  /*******************************************************************
   Fetch Data
  *******************************************************************/

  private fetchPublicWallets(): void {
    const request$ = this.isNewApiForTransactionType ?
      this.walletPublicService.getWalletListForTransaction(this.transactionType) :
      this.walletPublicService.getPublicList();

    request$
      .pipe(
        take(1),
        finalize(() => { this.isLoading = false; }))
      .subscribe(
        (wallets: Wallets) => {
          if (this.removeWalletId) {
            let mustRemoveIndex = wallets.fiat.list.findIndex(item => item.walletId === this.removeWalletId);
            if (mustRemoveIndex > -1) {
              wallets.fiat.list.splice(mustRemoveIndex, 1);
            } else {
              mustRemoveIndex = wallets.crypto.list.findIndex(item => item.walletId === this.removeWalletId);
              wallets.crypto.list.splice(mustRemoveIndex, 1);
            }
          }
          this.wallets = wallets;
          this.fiatWalletList = this.checkWalletLockState(wallets.fiat.list);
          this.cryptoWalletList = this.checkWalletLockState(wallets.crypto.list);
          this.isLoading = false;
        }
      );
  }

  private fetchDepositEMoneyWallets(): void {
    this.walletPublicService.getForDepositEMoney(this.serviceId)
      .pipe(
        take(1),
        finalize(() => { this.isLoading = false; }))
      .subscribe(
        (wallets: Wallets) => {
          this.wallets = wallets;
          this.fiatWalletList = this.checkWalletLockState(wallets.fiat.list);
          this.cryptoWalletList = this.checkWalletLockState(wallets.crypto.list);
        }
      );
  }

  private fetchWithdrawalEMoneyWallets(): void {
    this.walletPublicService.getForWithdrawalEMoney(this.serviceId)
      .pipe(
        take(1),
        finalize(() => { this.isLoading = false; }))
      .subscribe(
        (wallets: Wallets) => {
          this.wallets = wallets;
          this.fiatWalletList = this.checkWalletLockState(wallets.fiat.list);
          this.cryptoWalletList = this.checkWalletLockState(wallets.crypto.list);
        }
      );
  }

  private fetchInternalTransferWallets(): void {
    this.walletPublicService.getInternalTransferList()
      .pipe(
        take(1),
        finalize(() => { this.isLoading = false; }))
      .subscribe(
        (wallets: Wallets) => {
          this.wallets = wallets;
          this.fiatWalletList = this.checkWalletLockState(wallets.fiat.list);
          this.cryptoWalletList = this.checkWalletLockState(wallets.crypto.list);
          this.isLoading = false;
        }
      );
  }

  private fetchExchangeWallets(): void {
    const wallets = this.walletPublicService.getStoredExchangeWalletList();
    if (wallets && this.relationId) {
      this.manageWalletsForExchange(wallets);
      this.wallets = wallets;
      return;
    }
    this.walletPublicService.getExchangeWalletList()
      .pipe(
        take(1),
        finalize(() => { this.isLoading = false; }))
      .subscribe(
        (wallets: Wallets) => {
          this.wallets = wallets;
          this.manageWalletsForExchange(wallets);
        }
      );
  }

  /*******************************************************************
   Private
  *******************************************************************/

  private manageWalletsForExchange(wallets: Wallets): void {
    if (this.relationId) {
      let fiatList = [];
      let cryptoList = [];
      const allWallets = wallets.crypto.list.concat(wallets.fiat.list)

      // fill wallets
      wallets.currencyRelations[this.relationId].forEach(element => {
        allWallets.forEach(wallet => {
          if (wallet.currencyId === element) {
            if (wallet.currencyType === WalletType.Fiat && wallet.walletId !== this.fromWalletId) {

              fiatList.push(wallet);
            } else if (wallet.walletId !== this.fromWalletId) {
              cryptoList.push(wallet);
            }
          }
        });
      });

      this.fiatWalletList = this.checkWalletLockState(fiatList, false);
      this.cryptoWalletList = this.checkWalletLockState(cryptoList, false);
      this.isLoading = false;
      return;
    } else {
      this.fiatWalletList = [];
      this.cryptoWalletList = [];
      // fill fiat wallets
      wallets.fiat.list.forEach(element => {
        if (wallets.currencyRelations && wallets.currencyRelations[element.currencyId]) {
          this.fiatWalletList.push(element);
        }
      });
      this.fiatWalletList = this.checkWalletLockState(this.fiatWalletList, true);
      // fill crypto wallets
      wallets.crypto.list.forEach(element => {
        if (wallets.currencyRelations && wallets.currencyRelations[element.currencyId]) {
          this.cryptoWalletList.push(element);
        }
      });
      this.cryptoWalletList = this.checkWalletLockState(this.cryptoWalletList, true);
    }
  }
  private checkWalletLockState(wallets: WalletSelectList[], isFromWalletExchange?: boolean): WalletSelectList[] {
    wallets.forEach(wallet => {
      if (
        wallet.transactionState !== WalletTransactionState.CurrencyDisabled
      ) {
        wallet.transactionState = WalletTransactionState.Usable;
      }
    });
    return wallets;
    // wallets.forEach(wallet => {
    //   if (
    //     wallet.transactionState === WalletTransactionState.Usable ||
    //     wallet.transactionState === WalletTransactionState.CurrencyDisabled
    //   ) {
    //     return;
    //   }

    //   if (wallet.transactionState === WalletTransactionState.DepositLocked) {
    //     switch (this.requestType) {
    //       case RequestType.Deposit, RequestType.Point:
    //         break;
    //       case RequestType.Exchange:
    //         if (isFromWalletExchange) {
    //           wallet.transactionState = WalletTransactionState.Usable;
    //         }
    //       default:
    //         wallet.transactionState = WalletTransactionState.Usable;

    //     }
    //   } else if (wallet.transactionState === WalletTransactionState.WithdrawalLocked) {
    //     switch (this.requestType) {
    //       case RequestType.Withdrawal, RequestType.Send, RequestType.Voucher:
    //         break;
    //       case RequestType.Exchange:
    //         if (!isFromWalletExchange) {
    //           wallet.transactionState = WalletTransactionState.Usable;
    //         }
    //       default:
    //         wallet.transactionState = WalletTransactionState.Usable;

    //     }
    //   }
    // });
    // return wallets;
  }

  /*******************************************************************
   Get Data
  *******************************************************************/

  private getWallets(): void {
    if (!this.requestType) {
      this.fetchPublicWallets();
      return;
    }
    switch (this.requestType) {
      case RequestType.Deposit:
        this.fetchDepositEMoneyWallets();
        break;

      case RequestType.Withdrawal:
        this.fetchWithdrawalEMoneyWallets();
        break;

      case RequestType.Send:
        this.fetchInternalTransferWallets();
        break;

      case RequestType.Exchange:
        this.fetchExchangeWallets();
        break;
    }
  }


  /*******************************************************************
   Public
  *******************************************************************/

  selectWallet(wallet: WalletSelectList): void {
    if (wallet.transactionState !== WalletTransactionState.Usable) {
      return;
    }
    if (
      (wallet.currencyType == WalletType.Fiat && (this.wallets && !this.wallets.fiat.enable)) ||
      (wallet.currencyType == WalletType.Crypto && (this.wallets && !this.wallets.crypto.enable))
    ) {
      return;
    }
    this.selectedWalletEmit.emit(wallet);
  }

  onResized(event: ResizedEvent) {
    this.walletClass = this.getClass(event.newRect.width);
  }

  /*******************************************************************
   Style
  *******************************************************************/

  private getClass(width: number): string {
    if (width < 510) {
      return 'wallet-xs';

    } else if (width > 509 && width < 728) {
      return 'wallet-sm';

    } else if (width > 727 && width < 1027) {
      return ('wallet-md');

    } else if (width > 1026 && width < 1500) {
      return 'wallet-lg';

    } else if (width > 1499) {
      return 'wallet-xl';

    }
  }

  @HostListener('window:resize', ['$event'])
  onResize($event?) {
    if (window.innerWidth < 993) {
      this.isMobileSize = true;
    } else {
      this.isMobileSize = false;
    }

  }

}
